"use client"
import { useState, useEffect } from "react"
import axios from "axios"
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";


const AllVendorOrder = () => {
  const [orders, setOrders] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    fetchOrders()
  }, [])

  const parseOrderDate = (dateStr) => {
    if (!dateStr) return new Date(0);

    const [day, mon, year] = dateStr.split("-");
    const monthMap = {
      jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
      jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
    };

    return new Date(
      Number(year),
      monthMap[mon.toLowerCase()],
      Number(day)
    );
  };

  const fetchOrders = async () => {
    try {
      setIsLoading(true)
      const res = await axios.get(
        `https://logicx.premierlam.com/api/pixel-get-all-order-for-vendor`,
        { headers: { authorization: localStorage.getItem("accessToken") } }
      )
      const onlyConfirmed = (res.data.data || []).filter(o =>
        String(o.order_status).toLowerCase() !== "pending"
      ).sort(
        (a, b) =>
          parseOrderDate(b.order_date) - parseOrderDate(a.order_date)
      );
      setOrders(onlyConfirmed);

    } catch (err) {
      setError("Token expired. Please login again.")
      setOrders([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleStatusChange = async (order, newStatus) => {
    try {
      await axios.put(
        `https://logicx.premierlam.com/api/update-vendor-order-status/${order.id}`,
        { vendor_order_status: newStatus },
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
            "Content-Type": "application/json",
          },
        }
      );

      toast.success("Order status updated successfully!", {
        position: "top-right",
        autoClose: 2500,
        hideProgressBar: false,
        pauseOnHover: true,
        draggable: true,
      });

      fetchOrders(); // refresh table
    } catch (err) {
      toast.error("Failed to update status", {
        position: "top-right",
        autoClose: 2500,
      });
    }
  };


  const filteredOrders = orders.filter(
    (o) =>
      o.order_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.order_status.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.order_medium.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage)
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  )

  // const formatDate = (date) =>
  //   new Date(date).toLocaleDateString("en-US")
  const getRowColor = (status) => {
    const colors = {
      "Pending": "#f8d6d6",              // lighter red
      "Ready to ship": "#f3d3f0",        // lighter pink/purple
      "Order Confirmed": "#d6fff7",      // lighter teal
      "Order Picked up": "#f3f6c8",      // pale yellow-green
      "Order Delivered": "#d5f8d2",      // soft green
      "RTO Processed": "#e4e0f8",        // soft lavender
      "Order Returned": "#f1d7d7",       // soft rose
      "Customer Rejected": "#ffebd6",    // pale orange
      "Cancelled by Customer Before": "#ececec", // light grey
      "Cancelled by Customer After": "#f0dff7",  // light purple
      "Calculate Profit": "#f7e9c1",     // soft gold
    };

    return colors[status] || "transparent";
  };
  const getActionBg = (status) => {
    const map = {
      "pending": "rgba(220, 38, 38, 0.25)",          // red
      "ready to ship": "rgba(168, 85, 247, 0.25)",   // purple
      "order confirmed": "rgba(20, 184, 166, 0.25)", // teal
      "order picked up": "rgba(202, 138, 4, 0.25)",  // yellow
      "order delivered": "rgba(22, 163, 74, 0.25)",  // green
      "rto processed": "rgba(124, 58, 237, 0.25)",
      "order returned": "rgba(185, 28, 28, 0.25)",
      "customer rejected": "rgba(234, 88, 12, 0.25)",
      "cancelled by customer before": "rgba(75, 85, 99, 0.25)",
      "cancelled by customer after": "rgba(147, 51, 234, 0.25)",
      "calculate profit": "rgba(202, 138, 4, 0.25)",
    };

    return map[String(status).toLowerCase()] || "rgba(0,0,0,0.08)";
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={2500} />

      <div className="p-4 bg-gray-50 min-h-screen">

        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-900 mb-1">Vendor Orders</h1>
            <p className="text-gray-500">Orders assigned to this vendor</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none w-64 mb-4"
            />

            {successMessage && (
              <div className="mb-3 p-3 bg-green-100 border border-green-300 rounded text-green-700 font-medium">
                {successMessage}
              </div>
            )}

            {error && (
              <div className="mb-3 p-3 bg-red-100 border border-red-300 rounded text-red-700 font-medium">
                {error}
              </div>
            )}

            {/* TABLE */}
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="text-center py-6 text-gray-500">Loading...</div>
              ) : (
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-600 text-sm">
                      <th className="py-3 px-4 text-left">View Order</th>
                      <th className="py-3 px-4 text-left">Order ID</th>
                      <th className="py-3 px-4 text-left">Date</th>
                      <th className="py-3 px-4 text-left">Medium</th>
                      <th className="py-3 px-4 text-left">Order Status</th>
                      <th className="py-3 px-4 text-left">Order Value </th>
                      <th className="py-3 px-4 text-left">Vendor Payment Status</th>
                      <th className="py-3 px-4 text-left">Shipping Label</th>
                      <th className="py-3 px-4 text-left">Vendor Order Status</th>
                      <th className="py-3 px-4 text-left">Order Details</th>
                      {/* <th className="py-3 px-4 text-left">Items</th> */}
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedOrders.map((o) => (
                      // <tr key={o.id} className="border-b hover:bg-gray-50">
                      <tr
                        key={o.id}
                        className="border-b"
                        style={{ backgroundColor: getRowColor(o.order_status) }}
                      >

                        {/* NEW VIEW BUTTON */}
                        <td className="py-3 px-4">
                          <button
                            onClick={() => window.open(`/vendor-order/${o.order_id}`, "_blank")}
                            className="w-32 px-3 py-1 text-sm font-medium rounded"
                            style={{
                              backgroundColor: getActionBg(o.order_status),
                              color: "#111827", // dark gray text
                            }}

                          >
                            View order
                          </button>
                        </td>
                        <td className="py-3 px-4 text-sm">{o.order_id}</td>
                        <td className="py-3 px-4 text-sm whitespace-nowrap">{o.order_date}</td>
                        <td className="py-3 px-4 text-sm">{o.order_medium}</td>
                        <td className="py-3 px-4 text-sm">{o.order_status}</td>
                        <td className="py-3 px-4 text-sm">{o.order_value_for_vendor}</td>

                        <td className="py-3 px-4 text-sm">{o.vendor_payment_status}</td>
                        <td className="py-3 px-4 text-sm">
                          {o.shipping_label ? (
                            <a
                              href={o.shipping_label}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline hover:text-blue-800"
                            >
                              {o.shipping_label}
                            </a>
                          ) : (
                            "Not Available"
                          )}
                        </td>


                        {/* Vendor Order Status Dropdown */}
                        <td className="py-3 px-4 text-sm">
                          <select
                            value={o.vendor_order_status}
                            onChange={(e) => handleStatusChange(o, e.target.value)}
                            className="px-2 py-1 rounded text-sm border"
                            style={{
                              backgroundColor: getActionBg(o.order_status),
                              color: "#111827",
                              borderColor: "rgba(0,0,0,0.2)"
                            }}
                          >
                            <option value="packed">packed</option>
                            <option value="pending">Pending</option>
                          </select>
                        </td>

                        <td className="py-3 px-4 text-sm">{o.order_details}</td>
                        {/* <td className="py-3 px-4 text-sm">{o.number_of_items}</td> */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* PAGINATION */}
            <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
              <span>Page {currentPage} of {totalPages}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded-md disabled:opacity-50"
                >
                  Prev
                </button>
                <button
                  onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded-md disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default AllVendorOrder
